import {scrollToTopAnimations} from '@app/layout/scroll-top/animations/scroll-top.component.animations';
import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {AppService} from '@app/app.service';
import {HTMLInput} from '@interfaces/main/types';

@Component({
    selector: 'app-scroll-top [element]',
    templateUrl: './scroll-top.component.html',
    styleUrls: ['../styles/scroll-top.component.scss'],
    animations: [scrollToTopAnimations.showHide],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScrollTopComponent implements OnInit, OnDestroy, AfterViewInit  {
    public readonly appService: AppService = inject(AppService);
    readonly #elementRef: ElementRef = inject(ElementRef);
    readonly #renderer: Renderer2 = inject(Renderer2);

    #window: boolean =  false;
    /** Элемент, который скролиться */
    #element!: HTMLElement;

    @Input('element')
    public set element(el: HTMLInput | string) {
        switch (typeof el) {
            case 'string':
                if (el.length) {
                    this.#element = document.querySelector(el);
                }
                return;
            case 'object':
                this.#element = el;
                return;
        }
        this.#element = document.getElementsByTagName('html')[0];
        this.#window = true;

    }


    /** Флаг состояния отображения кнопки */
    private readonly _hidden$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    /** Событие скорла блока */
    private readonly _scrollEvent$: Subject<void> = new Subject();

    private readonly _destroy: Subject<void> = new Subject();

    /** Последние состояние скролла во время шага прокрутки */
    private _lastCurrentScroll: number = 0;

    private _scrollDirection: 'up' | 'down' = null;

    /** Вызываем событие прокрутки */
    private _scrollEvent = () => {

        if (this._lastCurrentScroll < this.#element?.scrollTop) {
            this._scrollDirection = 'down';
        } else {
            this._scrollDirection = 'up';
        }
        this._lastCurrentScroll = this.#element?.scrollTop;
        this._scrollEvent$.next();
    }

    private _smoothScroll = () => {
        const currentScroll = this.#element?.scrollTop;
        if (currentScroll && currentScroll > 0 && this._scrollDirection === 'up') {
            window.requestAnimationFrame(this._smoothScroll);
            this.#element.scrollTo(0, currentScroll - (currentScroll / 8));
            this._hidden$.next(true);
        } else if (currentScroll && currentScroll > 0 && this._scrollDirection === 'down') {
            this._hidden$.next(false);
        }
    }

    /** Получить состояния флага скрытия */
    public get hidden$(): Observable<boolean> {
        return this._hidden$.asObservable();
    }

    /** Прокрутить вверх блока */
    public scrollToTop(): void {
        this._scrollDirection = 'up';
        this._smoothScroll();
    }

    public ngOnInit(): void {
        if (this.#window) {
            window.addEventListener('scroll', this._scrollEvent);
        } else {
            this.#element.addEventListener('scroll', this._scrollEvent);
        }

        this._scrollEvent$
            .pipe(
                takeUntil(this._destroy),
                debounceTime(500)
            )
            .subscribe(() => {

                if (this.#element?.scrollTop && this.#element?.scrollTop > 100) {
                    this._hidden$.next(false);
                    return;
                } else if (!this._hidden$.value) {
                    this._hidden$.next(true);
                }
            });
    }

    public ngOnDestroy(): void {
        /** Удаляем прослушивание события прокрутки в блоке */
        this.#element.removeEventListener('scroll', this._scrollEvent);

        this._destroy.next();

        /** Завершаем работу потоков */
        this._hidden$.complete();
        this._scrollEvent$.complete();
        this._destroy.complete();
    }

    public ngAfterViewInit(): void {
        // Если компонент вешается на всё окно, то стиль изменяется
        if (this.#window) {
            this.#renderer.setStyle(this.#elementRef.nativeElement, 'position', 'fixed');
        }
    }
}
